import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Button, Flex, Popconfirm, Input, Spin, Drawer, DatePicker, Select, Modal, Image, Tag, Space } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { UserAddOutlined, EditOutlined, DeleteOutlined, MoreOutlined, CopyOutlined, DownloadOutlined, PrinterOutlined, ClockCircleOutlined, DollarOutlined, CheckCircleOutlined, CloseCircleOutlined, SendOutlined, EyeOutlined, FileTextOutlined, FileDoneOutlined, SnippetsOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom"
import * as actions from "store/actions/AuthActions"
import dayjs from 'dayjs';
import { Dropdown, message } from 'antd/lib';
import axios from 'axios';
import { copyText } from 'utils/clipboard';
import fileDownload from 'js-file-download';
import CloneInvoiceDialog from './CloneInvoiceDialog';
import ScheduleReminderModal from '../../pages/Invoices/ScheduleReminderModal';
import SendInvoiceDrawer from '../../pages/Invoices/SendInvoiceDrawer';
import * as adminactions from "store/actions/AuthActions";
import PreviewDrawerActions from '../Common/PreviewDrawerActions';
import PdfViewer from '../Common/PdfViewer';
import FilterBar, { FilterField } from '../Common/FilterBar';
import useUrlFilters from 'hooks/useUrlFilters';
import { formatMoney, recordCurrency } from 'utils/money';

const getStatusStyle = (status, dueDate) => {
  const statusLower = status?.toLowerCase();
  const isOverdue = dueDate && dayjs(dueDate).isBefore(dayjs(), 'day');

  // PARTIAL: red if overdue, orange otherwise
  if (statusLower === 'partial') {
    const color = isOverdue ? "#DC2626" : "#F97316";
    return { backgroundColor: `${color}20`, color: color };
  }

  const colors = {
    paid: "#16A34A",      // green
    sent: "#F97316",      // orange
    draft: "#DC2626",     // red
    ready: "#16A34A",     // green
    cancelled: "#6B7280", // grey
  };

  const color = colors[statusLower] || "#6B7280";
  return { backgroundColor: `${color}20`, color: color };
};

// Pre-defined style objects to avoid recreation on each render
const STATUS_BADGE_BASE = {
  padding: "4px 12px",
  borderRadius: "9999px",
  fontSize: "12px",
  fontWeight: 500,
  whiteSpace: 'nowrap',
};

const URGENCY_STYLES = {
  paid: { backgroundColor: '#16A34A20', color: '#16A34A', ...STATUS_BADGE_BASE },
  critical: { backgroundColor: '#991B1B20', color: '#991B1B', ...STATUS_BADGE_BASE },
  overdue: { backgroundColor: '#DC262620', color: '#DC2626', ...STATUS_BADGE_BASE },
  dueSoon: { backgroundColor: '#F59E0B20', color: '#F59E0B', ...STATUS_BADGE_BASE },
  onTime: { backgroundColor: '#16A34A20', color: '#16A34A', ...STATUS_BADGE_BASE },
};

// Reusable menu-item label: an icon + text row that stops row-click propagation
const menuItem = (icon, text, onClick, extraStyle = {}) => ({
  label: (
    <span
      onClick={(e) => { e.stopPropagation(); onClick(e); }}
      style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', ...extraStyle }}
    >
      <span style={{ marginRight: '8px', display: 'inline-flex' }}>{icon}</span>
      {text}
    </span>
  ),
  onClick: (e) => {
    e.domEvent.stopPropagation();
  },
});

const getItemsForDropDown = (record, handlers) => {
  const {
    handleDelete,
    copyPaymentLinkToNavigator,
    handleCloneClick,
    handleScheduleReminder,
    handleReviewManualPayment,
    handleViewInvoice,
    handlePrintInvoice,
    handleDownloadInvoice,
    handleSendInvoice,
    handleCreateCreditNote,
    handleCreateDebitNote,
    handleCreateQuote,
  } = handlers;

  const items = [
    {
      key: 'edit',
      label: (
        <a href={`/invoices/edit/${record.id}/`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
          <EditOutlined style={{ marginRight: '8px' }} />
          Edit
        </a>
      ),
      onClick: (e) => {
        e.domEvent.stopPropagation();
      }
    },
    { ...menuItem(<EyeOutlined />, 'View Invoice', () => handleViewInvoice(record)), key: 'view' },
  ];

  // PDF-based actions are only meaningful for auto-generated invoices
  if (!record.is_manual) {
    items.push({ ...menuItem(<PrinterOutlined />, 'Print Invoice', () => handlePrintInvoice(record)), key: 'print' });
    items.push({ ...menuItem(<DownloadOutlined />, 'Download Invoice', () => handleDownloadInvoice(record)), key: 'download' });
  }

  items.push({ ...menuItem(<SendOutlined />, 'Send Invoice', () => handleSendInvoice(record)), key: 'send' });

  items.push({ type: 'divider', key: 'div-payments' });

  items.push({
    key: 'record-payment',
    label: (<a href={`/payments/new?invoice_id=${record.id}&invoice_number=${record.inv_number}&account_id=${record.account}`} style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
      <DollarOutlined style={{ marginRight: '8px' }} />
      Record Payment
    </a>),
    onClick: (e) => {
      e.domEvent.stopPropagation();
    }
  });
  items.push({
    key: 'payments',
    label: (<a href={`/payments?search=${record.inv_number}`} style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
      <span style={{ marginRight: '8px' }}>💳</span>
      Payments
    </a>),
    onClick: (e) => {
      e.domEvent.stopPropagation();
    }
  });
  items.push({ ...menuItem(<span>📋</span>, 'Copy Payment Link', () => copyPaymentLinkToNavigator(record)), key: 'copy-link' });

  items.push({ type: 'divider', key: 'div-create' });

  items.push({ ...menuItem(<CopyOutlined />, 'Clone Invoice', () => handleCloneClick(record)), key: 'clone' });
  items.push({ ...menuItem(<FileTextOutlined />, 'Create Credit Note', () => handleCreateCreditNote(record)), key: 'credit-note' });
  items.push({ ...menuItem(<FileDoneOutlined />, 'Create Debit Note', () => handleCreateDebitNote(record)), key: 'debit-note' });
  items.push({ ...menuItem(<SnippetsOutlined />, 'Create Quote', () => handleCreateQuote(record)), key: 'create-quote' });

  items.push({ type: 'divider', key: 'div-misc' });

  items.push({ ...menuItem(<ClockCircleOutlined />, 'Schedule Reminder', () => handleScheduleReminder(record)), key: 'schedule-reminder' });

  // Add "Review Manual Payment" when manual payment is pending
  if (record.is_manual_payment_pending) {
    items.push({
      ...menuItem(<CheckCircleOutlined />, 'Review Manual Payment', () => handleReviewManualPayment(record), { color: '#F97316' }),
      key: 'review-manual',
    });
  }

  items.push({
    key: 'delete',
    label: (<Popconfirm title="Sure to delete?"
      onConfirm={() => handleDelete(record.id)}
      onClick={(e) => e.stopPropagation()}
    >
      <a style={{ display: 'flex', alignItems: 'center', color: '#DC2626' }} onClick={(e) => e.stopPropagation()}>
        <DeleteOutlined style={{ marginRight: '8px' }} />
        Delete
      </a>
    </Popconfirm>),
    onClick: (e) => {
      e.domEvent.stopPropagation();
    }
  });

  return items;
}

// Every filter, the search and the page live in the URL so a filtered list can
// be shared as a link. Param names match the old hand-rolled sync.
const URL_FILTERS = {
  search: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 20 },
  dateRange: { type: 'dateRange', keys: ['start_date', 'end_date'] },
  dueDateRange: { type: 'dateRange', keys: ['due_start_date', 'due_end_date'] },
  account: { type: 'string' },
  status: { type: 'array' },
  products: { type: 'array', key: 'product' },
  currency: { type: 'string' },
};

const InvoicesTable = () => {

  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });

  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter('search');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  const dateRange = filters.dateRange;
  const dueDateRange = filters.dueDateRange;
  const selectedAccount = filters.account;
  // Status and product are multi-select: a list matches invoices in any of them.
  const selectedStatus = filters.status;
  const selectedProducts = filters.products;
  const selectedCurrency = filters.currency;
  const [accounts, setAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [products, setProducts] = useState([]);

  // Backend pagination state
  const currentPage = filters.page;
  const setCurrentPage = urlSetter('page');
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = urlSetter('pageSize');
  const [totalRows, setTotalRows] = useState(0);

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  const [invoiceStatuses, setInvoiceStatuses] = useState([]);

  // State for drawer
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  // State for clone dialog
  const [cloneDialogVisible, setCloneDialogVisible] = useState(false);
  const [invoiceToClone, setInvoiceToClone] = useState(null);

  // State for schedule reminder modal
  const [scheduleReminderModalOpen, setScheduleReminderModalOpen] = useState(false);
  const [selectedInvoiceForReminder, setSelectedInvoiceForReminder] = useState(null);

  // State for send invoice drawer
  const [sendDrawerOpen, setSendDrawerOpen] = useState(false);
  const [selectedInvoiceForSend, setSelectedInvoiceForSend] = useState(null);

  const [, setPreviewPdfVisible] = useState(false);
  const [, setPdfPreviewData] = useState(null);

  // State for manual payment review modal
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewingInvoice, setReviewingInvoice] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  const hasRange = (range) => Boolean(range && range[0] && range[1]);

  const fetchInvoices = async (page, size) => {
    setLoading(true);
    try {
      const response = await actions.loadInvoices(page, searchTerm, dateRange, selectedAccount, selectedStatus, size, selectedCurrency, {
        products: selectedProducts,
        dueDateRange,
      });
      if (response && response.status === 200) {
        const data = response.data;
        setDataSource(data.results || []);
        setTotalRows(data.count || 0);
      }
    } catch (e) {
      console.error('Error fetching invoices:', e);
    }
    setLoading(false);
  }

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    } else {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page on search
    }
  }

  const loadInvoiceStatuses = async () => {
    const response = await actions.loadInvoiceStatuses();
    if (response) {
      setInvoiceStatuses(response);
    }
  }

  const loadAccounts = async () => {
    try {
      const response = await actions.loadAccounts(1, "", 1000);
      if (response?.status === 200) {
        setAccounts(response.data.results || []);
      }
    } catch (e) {
      console.log("Error loading accounts:", e);
    }
  }

  const loadProducts = async () => {
    try {
      const response = await actions.loadPaymentProducts();
      if (response?.status === 200) {
        setProducts(response.data.results || response.data || []);
      }
    } catch (e) {
      console.log("Error loading products:", e);
    }
  }

  // Every filter change goes back to page 1.
  const onFilterChange = (key) => (value) => setFilters({ [key]: value, page: 1 });

  const onDateRangeChange = onFilterChange('dateRange');
  const onDueDateRangeChange = onFilterChange('dueDateRange');
  const onAccountChange = onFilterChange('account');
  const onStatusChange = (value) => onFilterChange('status')(value || []);
  const onProductsChange = (value) => onFilterChange('products')(value || []);
  const onCurrencyChange = onFilterChange('currency');

  // Search sits outside the filter panel now, so it is not counted here and is
  // not touched by "Clear All".
  const activeFilterCount =
    (hasRange(dateRange) ? 1 : 0) +
    (hasRange(dueDateRange) ? 1 : 0) +
    (selectedAccount ? 1 : 0) +
    selectedStatus.length +
    selectedProducts.length +
    (selectedCurrency ? 1 : 0);

  const clearFilters = () => {
    setFilters({
      dateRange: null, dueDateRange: null, account: null, status: [], products: [], currency: null, page: 1,
    });
  };

  const loadCurrencies = async () => {
    try {
      const response = await axios.get('/masters/currencies/');
      if (response?.status === 200) {
        setCurrencies(response.data || []);
      }
    } catch (e) {
      console.log("Error loading currencies:", e);
    }
  }

  useEffect(() => {
    loadInvoiceStatuses();
    loadAccounts();
    loadCurrencies();
    loadProducts();
  }, []);

  // Fetch invoices when filters or pagination change
  useEffect(() => {
    fetchInvoices(currentPage, pageSize);
  }, [searchTerm, dateRange, dueDateRange, selectedAccount, selectedStatus, selectedProducts, selectedCurrency, currentPage, pageSize]);

  const handleDelete = useCallback(async (id) => {
    const newData = dataSource.find((item) => item.id === id);
    const response = await actions.deleteInvoices(newData.id);
    console.log(response);
    if (response.status === 204) {
      // Refresh the data after deletion
      fetchInvoices(currentPage, pageSize);
    }
  }, [dataSource, currentPage, pageSize]);

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
    },
  }

  const copyPaymentLinkToNavigator = useCallback(async (record) => {
    // If payment link already exists and is valid, copy it directly
    if (record.payment_link_url && record.is_payment_link_valid) {
      copyText(record.payment_link_url);
      messageApi.open({
        type: 'success',
        content: 'Payment link copied to clipboard.',
      });
      return;
    }

    // Otherwise generate a new payment link
    messageApi.open({ type: 'loading', content: 'Generating payment link...', key: 'paylink' });
    const result = await actions.generateInvoicePaymentLink(record.id);
    if (result && result.payment_link_url) {
      copyText(result.payment_link_url);
      messageApi.open({
        type: 'success',
        content: 'Payment link generated and copied to clipboard.',
        key: 'paylink',
      });
      // Refresh list to update payment_link_url in data
      fetchInvoices(currentPage, pageSize);
    } else {
      messageApi.open({
        type: 'error',
        content: 'Failed to generate payment link.',
        key: 'paylink',
      });
    }
  }, [messageApi, currentPage, pageSize]);

  const handleCloneClick = useCallback((record) => {
    setInvoiceToClone(record);
    setCloneDialogVisible(true);
  }, []);

  const handleCloneSuccess = (clonedInvoices) => {
    // Refresh the invoice list
    fetchInvoices(currentPage, pageSize);
    messageApi.open({
      type: 'success',
      content: `Successfully cloned invoice! Created ${clonedInvoices.length} new invoice(s).`,
    });
  };

  const handleScheduleReminder = useCallback((record) => {
    setSelectedInvoiceForReminder(record);
    setScheduleReminderModalOpen(true);
  }, []);

  const handleReviewManualPayment = useCallback((record) => {
    setReviewingInvoice(record);
    setReviewNotes('');
    setReviewModalOpen(true);
  }, []);

  const handleReviewAction = useCallback(async (actionType) => {
    if (!reviewingInvoice) return;
    setReviewLoading(true);
    const result = await actions.confirmInvoiceManualPayment(
      reviewingInvoice.id,
      actionType,
      reviewNotes
    );
    setReviewLoading(false);
    if (result) {
      messageApi.open({
        type: 'success',
        content: actionType === 'approve' ? 'Payment approved successfully.' : 'Payment rejected.',
      });
      setReviewModalOpen(false);
      setReviewingInvoice(null);
      fetchInvoices(currentPage, pageSize);
    } else {
      messageApi.open({
        type: 'error',
        content: 'Failed to process review action.',
      });
    }
  }, [reviewingInvoice, reviewNotes, messageApi, currentPage, pageSize]);

  const handleRowClick = useCallback(async (record) => {
    setSelectedInvoice(record); // Set selected invoice
    setDrawerVisible(true); // Open drawer
    setPdfLoading(true);
    try {
      const response = await axios.get(
        `invoices/invoices/${record.id}/invoiceView/`,
        { responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(response.data);
      setPdfUrl(blobUrl);
    } catch (error) {
      console.error('Error loading invoice PDF:', error);
      messageApi.error('Failed to load invoice preview');
    } finally {
      setPdfLoading(false);
    }
  }, [messageApi]);

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
  };

  const previewDownloadPdf = async (preview, download) => {
    const response = await adminactions.downloadInvoice(
      selectedInvoice?.id,
      true,
      "INVOICE_TEMPLATE_1"
    );
    console.log({ response });
    if (response) {
      if (download) {
        const filename = selectedInvoice?.inv_number ? `${selectedInvoice.inv_number}.pdf` : "invoice.pdf";
        fileDownload(response, filename);
      }
      if (preview) {
        setPdfPreviewData(response);
        setPreviewPdfVisible(true);
      }
    }
  };

  // View / Print both open the invoice-details drawer (which has a Print button),
  // mirroring the Actions dropdown on the invoice edit page.
  const handleViewInvoice = useCallback((record) => {
    handleRowClick(record);
  }, [handleRowClick]);

  const handlePrintInvoice = useCallback((record) => {
    handleRowClick(record);
  }, [handleRowClick]);

  const handleDownloadInvoice = useCallback(async (record) => {
    messageApi.open({ type: 'loading', content: 'Preparing download...', key: 'inv-dl' });
    try {
      const response = await adminactions.downloadInvoice(record.id, true, "INVOICE_TEMPLATE_1");
      if (response) {
        const filename = record?.inv_number ? `${record.inv_number}.pdf` : "invoice.pdf";
        fileDownload(response, filename);
        messageApi.open({ type: 'success', content: 'Invoice downloaded.', key: 'inv-dl' });
      } else {
        messageApi.open({ type: 'error', content: 'Failed to download invoice.', key: 'inv-dl' });
      }
    } catch (e) {
      messageApi.open({ type: 'error', content: 'Failed to download invoice.', key: 'inv-dl' });
    }
  }, [messageApi]);

  const handleSendInvoice = useCallback((record) => {
    setSelectedInvoiceForSend(record);
    setSendDrawerOpen(true);
  }, []);

  const handleCreateCreditNote = useCallback((record) => {
    navigate(`/credit-notes/new?invoice_id=${record.id}`);
  }, [navigate]);

  const handleCreateDebitNote = useCallback((record) => {
    navigate(`/debit-notes/new?invoice_id=${record.id}`);
  }, [navigate]);

  const handleCreateQuote = useCallback(async (record) => {
    try {
      const response = await adminactions.createQuoteFromInvoice(record.id);
      if (response) {
        messageApi.open({ type: 'success', content: `Quote ${response.qt_number} created successfully.` });
        navigate(`/quotes/edit/${response.id}`);
      }
    } catch (err) {
      messageApi.open({ type: 'error', content: 'Failed to create quote from invoice.' });
    }
  }, [messageApi, navigate]);

  const defaultColumns = useMemo(() => [
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: invoiceStatuses.map((is) => ({
        text: is[0],
        value: is[0]
      })),
      sorter: (a, b) => a?.status.localeCompare(b?.status),
      onFilter: (value, record) => record.status === value,
      render: (status, record) => (
        <Flex gap={4} align="center">
          <span style={{
            ...getStatusStyle(status, record.due_date),
            ...STATUS_BADGE_BASE,
          }}>
            {status}
          </span>
          {record.is_manual_payment_pending && (
            <Tag color="orange" style={{ fontSize: '10px', lineHeight: '16px', padding: '0 4px', margin: 0 }}>UPI</Tag>
          )}
        </Flex>
      ),
    },
    {
      title: 'NUMBER',
      dataIndex: 'inv_number',
      key: 'inv_number',
      sorter: (a, b) => a?.inv_number.localeCompare(b?.inv_number),
      width: 220,
      render: (inv_number, record) => (
        <a
          href={`/invoices/edit/${record.id}/`}
          onClick={(e) => e.stopPropagation()}
          style={{
            color: '#1890ff',
            textDecoration: 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >
          {inv_number}
        </a>
      ),
    },
    {
      title: 'ACCOUNT',
      dataIndex: ["account_data", "name"],
      key: 'account_name',
      sorter: (a, b) => (a?.account_data?.name || '').localeCompare(b?.account_data?.name || ''),
      width: 100,
      ellipsis: true,
    },
    {
      title: 'AMOUNT PAYABLE',
      dataIndex: 'total_payable',
      key: 'total_payable',
      sorter: (a, b) => (a?.inr_total || a?.total_payable || 0) - (b?.inr_total || b?.total_payable || 0),
      width: 140,
      align: 'right',
      render: (value, record) => {
        const iso = recordCurrency(record);
        const exRate = record.exchange_rate;
        const showInr = iso !== 'INR' && exRate && exRate != 1;
        return (
          <div>
            <span>{formatMoney(value, iso)}</span>
            {showInr && (
              <div style={{ fontSize: '11px', color: '#888' }}>
                ₹{(record.inr_total || Math.round(value * exRate))?.toLocaleString('en-IN')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'BALANCE',
      dataIndex: 'balance',
      key: 'balance',
      sorter: (a, b) => (a?.inr_balance || a?.balance || 0) - (b?.inr_balance || b?.balance || 0),
      width: 140,
      align: 'right',
      render: (value, record) => {
        const iso = recordCurrency(record);
        const exRate = record.exchange_rate;
        const showInr = iso !== 'INR' && exRate && exRate != 1;
        return (
          <div>
            <span>{formatMoney(value, iso)}</span>
            {showInr && (
              <div style={{ fontSize: '11px', color: '#888' }}>
                ₹{(record.inr_balance || Math.round(value * exRate))?.toLocaleString('en-IN')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'INR VALUE',
      dataIndex: 'inr_total',
      key: 'inr_value',
      sorter: (a, b) => (a?.inr_total || a?.total_payable || 0) - (b?.inr_total || b?.total_payable || 0),
      width: 140,
      align: 'right',
      render: (_, record) => {
        const iso = recordCurrency(record);
        const exRate = record.exchange_rate;
        if (iso === 'INR' || !exRate || exRate == 1) {
          return <span style={{ color: '#888' }}>-</span>;
        }
        const inrTotal = record.inr_total || Math.round(record.total_payable * exRate);
        const inrBalance = record.inr_balance || Math.round(record.balance * exRate);
        return (
          <div>
            <div>₹{inrTotal?.toLocaleString('en-IN')}</div>
            {record.balance > 0 && record.balance !== record.total_payable && (
              <div style={{ fontSize: '11px', color: '#888' }}>
                Bal: ₹{inrBalance?.toLocaleString('en-IN')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'DATE',
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      align: "right",
      width: 120,
      sorter: (a, b) => dayjs(a?.invoice_date) - dayjs(b?.invoice_date),
      render: (invoice_date) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: '9px', color: '#888', marginRight: '4px' }}>{dayjs(invoice_date).format("ddd")}</span>
          <span>{dayjs(invoice_date).format("YYYY-MM-DD")}</span>
        </div>
      )
    },
    {
      title: 'DUE DATE',
      dataIndex: 'due_date',
      key: 'due_date',
      align: "right",
      width: 120,
      sorter: (a, b) => dayjs(a?.due_date) - dayjs(b?.due_date),
      render: (due_date) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: '9px', color: '#888', marginRight: '4px' }}>{dayjs(due_date).format("ddd")}</span>
          <span>{dayjs(due_date).format("YYYY-MM-DD")}</span>
        </span>
      )
    },
    {
      title: 'URGENCY STATUS',
      dataIndex: 'due_date',
      key: 'urgency_status',
      align: 'center',
      width: 180,
      sorter: (a, b) => {
        const getUrgencyLevel = (record) => {
          if (record.status === 'PAID') return 0;
          const daysUntilDue = dayjs(record.due_date).diff(dayjs(), 'days');
          if (daysUntilDue < -30) return 4;
          if (daysUntilDue < 0) return 3;
          if (daysUntilDue <= 7) return 2;
          return 1;
        };
        return getUrgencyLevel(a) - getUrgencyLevel(b);
      },
      render: (due_date, record) => {
        if (record.status === 'PAID') {
          return <span style={URGENCY_STYLES.paid}>Paid</span>;
        }
        const daysUntilDue = dayjs(due_date).diff(dayjs(), 'days');
        const overdueDays = Math.abs(daysUntilDue);

        if (daysUntilDue < -30) {
          return <span style={URGENCY_STYLES.critical}>Critical ({overdueDays} days)</span>;
        }
        if (daysUntilDue < 0) {
          return <span style={URGENCY_STYLES.overdue}>Overdue ({overdueDays} days)</span>;
        }
        if (daysUntilDue <= 7) {
          const label = daysUntilDue === 0 ? 'Due Today' : `Due Soon (${daysUntilDue} days)`;
          return <span style={URGENCY_STYLES.dueSoon}>{label}</span>;
        }
        return <span style={URGENCY_STYLES.onTime}>On Time</span>;
      }
    },
    {
      title: 'TYPE',
      dataIndex: 'is_manual',
      key: 'is_manual',
      align: 'center',
      width: 90,
      render: (_, record) => (
        <span style={{
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          backgroundColor: record.is_manual ? '#f0f0f0' : '#e6f7ff',
          color: record.is_manual ? '#666' : '#1890ff'
        }}>
          {record.is_manual ? "Manual" : "Auto"}
        </span>
      )
    },
    {
      title: 'ATTACHMENTS',
      dataIndex: 'attachments_count',
      key: 'attachments_count',
      align: 'center',
      width: 110,
      sorter: (a, b) => (a.attachments_count || 0) - (b.attachments_count || 0),
      render: (count) => (
        <span style={{
          padding: '2px 10px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 500,
          backgroundColor: count > 0 ? '#1890ff20' : '#f0f0f0',
          color: count > 0 ? '#1890ff' : '#999'
        }}>
          {count || 0}
        </span>
      )
    },
    {
      title: 'IS SCHEDULED',
      dataIndex: 'is_scheduled',
      key: 'is_scheduled',
      align: 'center',
      width: 140,
      render: (_, record) => (
        <span style={{ color: record.is_scheduled ? '#52c41a' : '#8c8c8c' }}>
          {record.is_scheduled ? "Yes" : "No"}
        </span>
      )
    },
    {
      title: 'PAYMENT RECEIPTS',
      dataIndex: 'payment_count',
      key: 'payment_count',
      align: 'center',
      width: 160,
      sorter: (a, b) => (a.payment_count || 0) - (b.payment_count || 0),
      render: (payment_count) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '9999px',
          fontSize: '12px',
          fontWeight: 500,
          backgroundColor: payment_count > 0 ? '#52c41a20' : '#8c8c8c20',
          color: payment_count > 0 ? '#52c41a' : '#8c8c8c'
        }}>
          {payment_count > 0 ? `Generated (${payment_count})` : 'None'}
        </span>
      )
    },
    {
      title: 'Actions',
      dataIndex: 'edit',
      key: 'edit',
      width: 220,
      render: (_, record) => (
        dataSource?.length >= 1 ? (
          <Flex gap="middle" style={{ padding: 5 }}>
            <Dropdown
              menu={{ items: getItemsForDropDown(record, {
                handleDelete,
                copyPaymentLinkToNavigator,
                handleCloneClick,
                handleScheduleReminder,
                handleReviewManualPayment,
                handleViewInvoice,
                handlePrintInvoice,
                handleDownloadInvoice,
                handleSendInvoice,
                handleCreateCreditNote,
                handleCreateDebitNote,
                handleCreateQuote,
              }) }}
              placement="bottomLeft"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreOutlined onClick={(e) => e.stopPropagation()} />
            </Dropdown>
          </Flex>
        ) : null
      )
    },
  ], [invoiceStatuses, navigate, handleDelete, copyPaymentLinkToNavigator, handleCloneClick, handleScheduleReminder, handleReviewManualPayment, handleViewInvoice, handlePrintInvoice, handleDownloadInvoice, handleSendInvoice, handleCreateCreditNote, handleCreateDebitNote, handleCreateQuote, dataSource?.length]);

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };


  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const [selectionType] = useState('checkbox');

  // Handle pagination change
  const handleTableChange = (page, size) => {
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    } else {
      setCurrentPage(page);
    }
  };


  return (
    <div style={{ padding: 24 }}>
      {contextHolder}
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <div>
          Showing {totalRows === 0 ? 0 : ((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRows)} of {totalRows} invoices
        </div>
      </Flex>
      <FilterBar
        searchValue={searchTermLive}
        onSearchChange={setSearchTermLive}
        onSearch={onSearch}
        searchPlaceholder="Search invoices"
        activeCount={activeFilterCount}
        onClear={clearFilters}
        title="Filter Invoices"
        onRefresh={() => fetchInvoices(currentPage, pageSize)}
        refreshing={loading}
        addLabel="Create Invoice"
        addIcon={<UserAddOutlined />}
        onAdd={() => navigate("/invoices/new")}
        addClassName="create-invoice-btn"
      >
        <FilterField label="Invoice Date">
          <DatePicker.RangePicker
            placeholder={['Start Date', 'End Date']}
            value={dateRange}
            onChange={onDateRangeChange}
          />
        </FilterField>
        <FilterField label="Due Date">
          <DatePicker.RangePicker
            placeholder={['Start Date', 'End Date']}
            value={dueDateRange}
            onChange={onDueDateRangeChange}
          />
        </FilterField>
        <FilterField label="Account">
          <Select
            placeholder="Select Account"
            value={selectedAccount}
            onChange={onAccountChange}
            showSearch
            optionFilterProp="children"
            allowClear
          >
            {accounts.map((account) => (
              <Select.Option key={account.id} value={account.id}>
                {account.name}
              </Select.Option>
            ))}
          </Select>
        </FilterField>
        <FilterField label="Status">
          <Select
            mode="multiple"
            placeholder="Select Status"
            value={selectedStatus}
            onChange={onStatusChange}
            allowClear
            options={invoiceStatuses.map((status) => ({ value: status[0], label: status[0] }))}
          />
        </FilterField>
        <FilterField label="Product">
          <Select
            mode="multiple"
            placeholder="Select Product"
            value={selectedProducts}
            onChange={onProductsChange}
            allowClear
            optionFilterProp="label"
            options={products.map((product) => ({ value: product.id, label: product.name }))}
          />
        </FilterField>
        <FilterField label="Currency">
          <Select
            placeholder="Currency"
            value={selectedCurrency}
            onChange={onCurrencyChange}
            showSearch
            optionFilterProp="children"
            allowClear
          >
            {currencies.map((currency) => (
              <Select.Option key={currency.id} value={currency.id}>
                {currency.iso_code}
              </Select.Option>
            ))}
          </Select>
        </FilterField>
      </FilterBar>
      <div>
        <Table
          className="invoices-table"
          rowSelection={{
            type: selectionType,
            ...rowSelection,
          }}
          loading={loading}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          size="small"
          dataSource={dataSource}
          columns={columns}
          rowKey={row => row.id}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRows,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ['50', '100', '200'],
            onChange: handleTableChange,
          }}
          scroll={{
            x: 'max-content',        // Enables horizontal scroll
            y: 'calc(100vh - 350px)' // Vertical scroll
          }}
          sticky
        />
      </div>

      {/* Drawer for Invoice Details */}
      <Drawer
        title={`Invoice Details - ${selectedInvoice?.inv_number ?? ""}`}
        placement="right"
        onClose={handleDrawerClose}
        open={drawerVisible}
        width={"50%"}
        extra={
          <PreviewDrawerActions
            onPrint={() => {
              if (pdfUrl) {
                const printWindow = window.open(pdfUrl);
                if (printWindow) {
                  printWindow.addEventListener('load', () => {
                    printWindow.print();
                  });
                }
              }
            }}
            printDisabled={!pdfUrl || pdfLoading}
            onDownload={() => previewDownloadPdf(false, true)}
            onClose={handleDrawerClose}
          />
        }
      >
        {/* Render invoice details here */}
        {/* Embed PDF */}
        {pdfLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spin size="large" />
          </div>
        ) : pdfUrl && (
          <PdfViewer
            url={pdfUrl}
            title="Invoice PDF"
            onDownload={() => previewDownloadPdf(false, true)}
          />
        )}
      </Drawer>

      {/* Send Invoice Drawer */}
      <Drawer
        title="Send Invoice"
        placement="right"
        size="large"
        onClose={() => { setSendDrawerOpen(false); setSelectedInvoiceForSend(null); }}
        open={sendDrawerOpen}
        width="50%"
        extra={
          <Space>
            <Button onClick={() => { setSendDrawerOpen(false); setSelectedInvoiceForSend(null); }}>Cancel</Button>
          </Space>
        }
      >
        {sendDrawerOpen && selectedInvoiceForSend && (
          <SendInvoiceDrawer instance={selectedInvoiceForSend} />
        )}
      </Drawer>

      {/* Clone Invoice Dialog */}
      <CloneInvoiceDialog
        visible={cloneDialogVisible}
        onClose={() => setCloneDialogVisible(false)}
        invoice={invoiceToClone}
        onSuccess={handleCloneSuccess}
      />

      {/* Schedule Reminder Modal */}
      <ScheduleReminderModal
        visible={scheduleReminderModalOpen}
        onClose={() => {
          setScheduleReminderModalOpen(false);
          setSelectedInvoiceForReminder(null);
          fetchInvoices(currentPage, pageSize);
        }}
        onSave={() => {
          setScheduleReminderModalOpen(false);
          setSelectedInvoiceForReminder(null);
          fetchInvoices(currentPage, pageSize);
        }}
        invoiceId={selectedInvoiceForReminder?.id}
        paymentTermId={selectedInvoiceForReminder?.payment_term}
        invoiceDueDate={selectedInvoiceForReminder?.due_date ? dayjs(selectedInvoiceForReminder.due_date) : null}
        invoiceDisplay={selectedInvoiceForReminder?.inv_number}
      />

      {/* Manual Payment Review Modal */}
      <Modal
        title={`Review Manual Payment - ${reviewingInvoice?.inv_number || ''}`}
        open={reviewModalOpen}
        onCancel={() => { setReviewModalOpen(false); setReviewingInvoice(null); }}
        footer={null}
        width={600}
      >
        {reviewingInvoice && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {reviewingInvoice.manual_payment_screenshot && (
              <div>
                <div style={{ fontWeight: 500, marginBottom: 8 }}>Payment Screenshot</div>
                <Image
                  src={reviewingInvoice.manual_payment_screenshot}
                  alt="Payment Screenshot"
                  style={{ maxHeight: 300, objectFit: 'contain', borderRadius: 8, border: '1px solid #d9d9d9' }}
                />
              </div>
            )}
            {reviewingInvoice.manual_payment_reference && (
              <div>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>Reference / UTR</div>
                <div style={{ fontFamily: 'monospace', background: '#f5f5f5', padding: '8px 12px', borderRadius: 6 }}>
                  {reviewingInvoice.manual_payment_reference}
                </div>
              </div>
            )}
            {reviewingInvoice.manual_payment_submitted_at && (
              <div>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>Submitted On</div>
                <div>{dayjs(reviewingInvoice.manual_payment_submitted_at).format('DD MMM YYYY, hh:mm A')}</div>
              </div>
            )}
            <div>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>Notes</div>
              <Input.TextArea
                rows={2}
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add a note (optional)"
              />
            </div>
            <Flex gap={12} justify="flex-end">
              <Button
                danger
                icon={<CloseCircleOutlined />}
                loading={reviewLoading}
                onClick={() => handleReviewAction('reject')}
              >
                Reject
              </Button>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={reviewLoading}
                onClick={() => handleReviewAction('approve')}
                style={{ background: '#16A34A', borderColor: '#16A34A' }}
              >
                Approve Payment
              </Button>
            </Flex>
          </div>
        )}
      </Modal>
    </div>
  );
};
export default InvoicesTable;
